import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Download, Filter, Info, BarChart3 } from 'lucide-react';
import { generateTakeRateData, formatPercentage, exportChart } from '../utils/visualization';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface TakeRateComparisonProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

export const TakeRateComparison: React.FC<TakeRateComparisonProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'rate'>('rate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const data = useMemo(() => {
    let result = generateTakeRateData(filters);
    
    // Apply sorting
    result.sort((a, b) => {
      const aValue = sortBy === 'name' ? a.companyName : a.value;
      const bValue = sortBy === 'name' ? b.companyName : b.value;
      
      if (sortBy === 'name') {
        return sortOrder === 'asc' 
          ? (aValue as string).localeCompare(bValue as string)
          : (bValue as string).localeCompare(aValue as string);
      } else {
        return sortOrder === 'asc' 
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });
    
    return result;
  }, [filters, sortBy, sortOrder]);

  // Business model descriptions available for future use
  // const businessModelInfo = {
  //   commission_based: 'Platform takes a percentage of each transaction',
  //   lead_generation: 'Platform charges for connecting customers with providers',
  //   transaction_fee: 'Fixed fee per transaction regardless of value',
  //   hybrid: 'Combination of multiple revenue models',
  //   subscription: 'Recurring monthly/annual fees from providers',
  // };

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('take-rate-comparison-chart');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'take-rate-comparison',
      includeData: true,
    };

    await exportChart(config, chartElement, data);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
          <h3 className="font-semibold text-gray-900 mb-2">{label}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Take Rate:</span>
              <span className="font-medium">{formatPercentage(data.value)}</span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500">
                Business Model: Commission-based platforms typically charge 10-20%, 
                while lead generation models can charge 20-30% per lead.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate statistics
  const avgTakeRate = data.reduce((sum, item) => sum + item.value, 0) / data.length;
  const maxTakeRate = Math.max(...data.map(item => item.value));
  const minTakeRate = Math.min(...data.map(item => item.value));

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Take Rate Comparison</h2>
          <p className="text-sm text-gray-600 mt-1">
            Platform economics and revenue models
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Average</span>
          </div>
          <div className="text-2xl font-bold text-green-900">
            {formatPercentage(avgTakeRate)}
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Highest</span>
          </div>
          <div className="text-2xl font-bold text-blue-900">
            {formatPercentage(maxTakeRate)}
          </div>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-900">Lowest</span>
          </div>
          <div className="text-2xl font-bold text-amber-900">
            {formatPercentage(minTakeRate)}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.company || ''}
                onChange={(e) => onFilterChange?.({ ...filters, company: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Companies</option>
                <option value="thumbtack">Thumbtack</option>
                <option value="taskrabbit">TaskRabbit</option>
                <option value="angi">Angi</option>
                <option value="handy">Handy</option>
                <option value="porch_group">Porch Group</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'rate')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="rate">Take Rate</option>
                <option value="name">Company Name</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">High to Low</option>
                <option value="asc">Low to High</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div id="take-rate-comparison-chart" className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="category"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis 
              label={{ value: 'Take Rate (%)', angle: -90, position: 'insideLeft' }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Business Model Information */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-2">Understanding Take Rates</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p>
                <strong>Commission-based (15-20%):</strong> TaskRabbit, Handy - Platform takes percentage of transaction value
              </p>
              <p>
                <strong>Lead generation (20-30%):</strong> Thumbtack - Platform charges for connecting customers with providers
              </p>
              <p>
                <strong>Hybrid models (10-15%):</strong> Angi, Porch - Combination of leads, subscriptions, and commissions
              </p>
              <p className="pt-2 text-xs">
                * Take rates shown are estimates based on business models and industry benchmarks. 
                Actual rates may vary by service category and market conditions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Company</th>
                <th className="px-4 py-3 text-right font-medium text-gray-900">Take Rate</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Business Model</th>
                <th className="px-4 py-3 text-center font-medium text-gray-900">Color</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{item.companyName}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatPercentage(item.value)}</td>
                  <td className="px-4 py-3 text-gray-700 capitalize">
                    {item.companyId.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div 
                      className="w-4 h-4 rounded mx-auto"
                      style={{ backgroundColor: item.color }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};